import { ApolloLink, Operation } from '@apollo/client'
import { Observable } from '@apollo/client/utilities'
import { print } from 'graphql'

import { getAPIConfig } from '@/utils/apiConfig'
import { getAccessToken } from '@/utils/auth'

// Async utility to construct the AppSync WebSocket URL
async function constructWebSocketUrl() {
  const apiConfig = await getAPIConfig()
  const token = await getAccessToken()

  const wsEndpoint = apiConfig.realtime
  const header = btoa(JSON.stringify({ host: apiConfig.host, authorization: token }))
  const payload = btoa('{}')
  return { url: `${wsEndpoint}?header=${header}&payload=${payload}`, host: apiConfig.host, authorization: token }
}

// Message types for AppSync protocol
enum MessageTypes {
  CONNECTION_INIT = 'connection_init',
  CONNECTION_ACK = 'connection_ack',
  START = 'start',
  START_ACK = 'start_ack',
  STOP = 'stop',
  KA = 'ka',
  DATA = 'data',
  ERROR = 'error',
  COMPLETE = 'complete',
}

// Type for subscription data
interface Subscription {
  payload: string
  callback: (message: { data?: unknown; errors?: unknown }) => void
}

// Type for WebSocket message
interface WebSocketMessage {
  type: MessageTypes
  id?: string
  payload?: { data?: unknown; errors?: unknown }
}

interface Extensions {
  authorization: { host: string; authorization: string | null }
}

// Core WebSocket client with retry and dynamic URL
class AppSyncWebSocket {
  private ws: WebSocket | null = null
  private subscriptions = new Map<string, Subscription>()
  private isConnected = false
  private url: string | null = null
  private retryCount = 0
  private maxRetries = 10
  private maxDelay = 100_000 // 100 seconds max delay
  private extensions: Extensions | null = null
  private reconnectTimeout: NodeJS.Timeout | null = null

  constructor() {
    void this.connect()
  }

  private async connect(): Promise<void> {
    const config = await constructWebSocketUrl()
    this.url = config.url
    this.extensions = { authorization: { authorization: config.authorization, host: config.host } }
    this.ws = new WebSocket(this.url, ['graphql-ws'])
    this.setupWebSocket()
  }

  private setupWebSocket(): void {
    if (!this.ws) return

    this.ws.onopen = () => {
      this.ws!.send(JSON.stringify({ type: MessageTypes.CONNECTION_INIT }))
    }

    this.ws.onmessage = (event: MessageEvent) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument
      const message: WebSocketMessage = JSON.parse(event.data)
      switch (message.type) {
        case MessageTypes.CONNECTION_ACK:
          this.isConnected = true
          console.log('Connection established')
          this.resubscribeAll()
          break
        case MessageTypes.START_ACK:
          console.log('Subscription acknowledged:', message.id)
          break
        case MessageTypes.DATA: {
          const sub = this.subscriptions.get(message.id ?? '')
          if (sub && message.payload?.data) {
            sub.callback({ data: message.payload.data })
          }
          break
        }
        case MessageTypes.KA:
          console.log('Keep-alive received')
          break
        case MessageTypes.ERROR: {
          console.error('Error:', message.payload)
          const errorSub = this.subscriptions.get(message.id ?? '')
          if (errorSub && message.payload) errorSub.callback({ errors: message.payload.errors })
          break
        }
        case MessageTypes.COMPLETE:
          this.subscriptions.delete(message.id ?? '')
          console.log('Subscription completed:', message.id)
          break
      }
    }

    this.ws.onerror = (error: Event) => {
      console.error('WebSocket error:', error)
      this.scheduleReconnect()
    }

    this.ws.onclose = () => {
      this.isConnected = false
      console.log('WebSocket closed')
      this.scheduleReconnect()
    }
  }

  private scheduleReconnect(): void {
    if (this.retryCount >= this.maxRetries || this.reconnectTimeout) return

    const delay = Math.min(1000 * Math.pow(2, this.retryCount), this.maxDelay)
    this.retryCount += 1
    console.log(`Reconnecting in ${delay / 1000} seconds (attempt ${this.retryCount}/${this.maxRetries})`)

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null
      void this.connect()
    }, delay)
  }

  private resubscribeAll(): void {
    if (!this.ws || !this.isConnected) return
    this.subscriptions.forEach((sub, id) => {
      this.ws!.send(
        JSON.stringify({
          id,
          type: MessageTypes.START,
          payload: { data: sub.payload, extensions: this.extensions },
        }),
      )
    })
  }

  public async reconnect(): Promise<void> {
    await this.connect()
  }

  public async refreshExtensions(): Promise<void> {
    const config = await constructWebSocketUrl()
    this.extensions = { authorization: { authorization: config.authorization, host: config.host } }
  }

  public subscribe(
    id: string,
    payload: string,
    callback: (message: { data?: unknown; errors?: unknown }) => void,
  ): void {
    this.subscriptions.set(id, { payload, callback })
    if (this.isConnected && this.ws) {
      this.ws.send(
        JSON.stringify({
          id,
          type: MessageTypes.START,
          payload: { data: payload, extensions: this.extensions },
        }),
      )
    } else {
      console.log('Subscription queued until connection is established')
    }
  }

  public unsubscribe(id: string): void {
    if (this.ws && this.isConnected) {
      this.ws.send(JSON.stringify({ id, type: MessageTypes.STOP, extensions: this.extensions }))
    }
    this.subscriptions.delete(id)
  }

  public close(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }
    if (this.ws) {
      this.subscriptions.forEach((_, id) => this.unsubscribe(id))
      this.ws.close()
    }
  }
}

// Apollo Link for AppSync WebSocket
export class AppSyncWebSocketLink extends ApolloLink {
  private wsClient: AppSyncWebSocket

  constructor() {
    super()
    this.wsClient = new AppSyncWebSocket()
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public request(operation: Operation): Observable<any> {
    return new Observable((observer) => {
      const id = `${Date.now()}-${Math.random()}`
      const payload = JSON.stringify({
        query: print(operation.query),
        variables: operation.variables,
      })

      this.wsClient.subscribe(id, payload, (message) => {
        if (message.data) {
          observer.next({ data: message.data })
        } else if (message.errors) {
          observer.next({ errors: message.errors ?? [] })
        }
      })

      return () => {
        this.wsClient.unsubscribe(id)
      }
    })
  }

  public async reconnect(): Promise<void> {
    await this.wsClient.reconnect()
  }

  public async refreshToken(): Promise<void> {
    await this.wsClient.refreshExtensions()
  }
}
